import { useCallback, useMemo, useRef, useState } from 'react';
import { AuthenticatorBuilder, OtpFinishResultType, DeviceRole } from '@prove-identity/prove-auth';
import { useMutation } from '@tanstack/react-query';
import { AuthMutationKeys } from '~/domains/auth/constants/query';
import { errorToString } from '~/domains/auth/utils/prefillKyc.utils';

import {
  initializeProvePrefill,
  verifyProvePrefill,
} from '~/services/identity/authenticated/prove-prefill.service.api';
import type {
  ProveFlowType,
  ProvePrefillInitializeInput,
  ProvePrefillVerifyResponse,
} from '~/services/identity/authenticated/prove-prefill.service.api';
import { Routes } from '~/domains/common/constants/routes';

export const enum ProvePrefillStatus {
  Idle = 'idle',
  Initializing = 'initializing',
  Authenticating = 'authenticating',
  AwaitingOtp = 'awaiting_otp',
  AwaitingInstantLink = 'awaiting_instant_link',
  Verifying = 'verifying',
  Completed = 'completed',
  Error = 'error',
}

export const enum VerifyType {
  Prove = 'prove',
  Manual = 'manual',
}

export interface UseProvePrefillOptions {
  onRequestOtpCode?: (context: { otpErrorMessage?: string }) => Promise<string | null>;
  onInitialized?: (authToken: string) => void;
  onVerify?: (verifyResponse: ProvePrefillVerifyResponse) => void;
  onError?: (errorMessage: string) => void;
}

export interface StartProvePrefillInput extends Omit<ProvePrefillInitializeInput, 'flowType'> {
  flowType?: ProveFlowType;
}

export function useProvePrefill(options?: UseProvePrefillOptions) {
  const [status, setStatus] = useState<ProvePrefillStatus>(ProvePrefillStatus.Idle);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<ProvePrefillVerifyResponse | null>(null);
  const otpResolverRef = useRef<null | ((otp: string) => void)>(null);

  const initializeMutation = useMutation({
    mutationKey: AuthMutationKeys.PROVE_PREFILL_INITIALIZE(),
    mutationFn: initializeProvePrefill,
  });
  const verifyMutation = useMutation({
    mutationKey: AuthMutationKeys.PROVE_PREFILL_VERIFY(),
    mutationFn: verifyProvePrefill,
  });

  const isMobileWeb = useMemo(() => {
    try {
      if (typeof window === 'undefined') return false;
      const authCheck = new AuthenticatorBuilder().build();
      return Boolean(authCheck.isMobileWeb());
    } catch {
      return false;
    }
  }, []);

  const toErrorString = useCallback((e: unknown) => errorToString(e), []);

  const startProvePrefill = useCallback(
    async (input: StartProvePrefillInput): Promise<void> => {
      setStatus(ProvePrefillStatus.Initializing);
      setError(null);
      setResult(null);

      try {
        const flowType: ProveFlowType = input.flowType ?? (isMobileWeb ? 'mobile' : 'desktop');

        // 1) Initialize on backend to retrieve our prove authToken
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        // const anonymousId = typeof window !== 'undefined' ? getAnonymousId() : undefined; // TODO: add anonymous id to the query string (needs backend validation update to allow over 128 chars)
        const finalTargetUrl = `${origin}${Routes.proveSuccess()}`;
        const { authToken } = await initializeMutation.mutateAsync({
          ...input,
          flowType,
          finalTargetUrl,
        });
        if (!authToken) {
          setStatus(ProvePrefillStatus.Error);
          options?.onError?.('Missing authToken from Prove initialize');
          return;
        }
        options?.onInitialized?.(authToken);

        // 2) Build authenticator (this will be different depending on mobile or desktop)
        let builder = new AuthenticatorBuilder();

        builder = builder.withAuthFinishStep(async () => {
          setStatus(ProvePrefillStatus.Verifying);
          try {
            const verifyResponse = await verifyMutation.mutateAsync();
            setResult(verifyResponse);
            options?.onVerify?.(verifyResponse);
          } catch (verifyError) {
            setResult(null);
            setStatus(ProvePrefillStatus.Error);
            options?.onError?.(toErrorString(verifyError));
          }
          return null;
        });

        if (flowType === 'mobile') {
          const otpStart = () =>
            new Promise<null>((resolve) => {
              resolve(null);
            });

          const otpFinish = (otpError?: { message?: string }) =>
            new Promise<{ input: { otp: string }; resultType: number }>((resolve, reject) => {
              setStatus(ProvePrefillStatus.AwaitingOtp);
              otpResolverRef.current = (otp: string) => {
                resolve({ input: { otp }, resultType: OtpFinishResultType.OnSuccess });
                otpResolverRef.current = null;
              };

              (async () => {
                try {
                  const otp = await options?.onRequestOtpCode?.({
                    otpErrorMessage: otpError?.message,
                  });

                  if (otp) {
                    otpResolverRef.current?.(otp);
                  }
                } catch (e) {
                  otpResolverRef.current = null;
                  reject(e);
                }
              })();
            });

          builder = builder.withOtpFallback(otpStart, otpFinish);
        } else {
          const instantLinkStart = () =>
            new Promise<null>((resolve) => {
              setStatus(ProvePrefillStatus.AwaitingInstantLink);
              resolve(null);
            });

          builder = builder
            .withInstantLinkFallback(instantLinkStart)
            .withRole(DeviceRole.Secondary);
        }

        const authenticator = builder.build();

        setStatus(ProvePrefillStatus.Authenticating);
        await authenticator.authenticate(authToken);
        setStatus(ProvePrefillStatus.Completed);
      } catch (e) {
        setError(e);
        setStatus(ProvePrefillStatus.Error);
        options?.onError?.(toErrorString(e));
      }
    },
    [
      isMobileWeb,
      options,
      initializeMutation.mutateAsync,
      verifyMutation.mutateAsync,
      toErrorString,
    ]
  );

  const reset = useCallback(() => {
    setStatus(ProvePrefillStatus.Idle);
    setError(null);
    setResult(null);
    otpResolverRef.current = null;
  }, []);

  const submitOtpCode = useCallback((otp: string) => {
    if (!otp) {
      throw new Error('OTP is empty');
    }
    const resolver = otpResolverRef.current;
    if (resolver) {
      resolver(otp);
      otpResolverRef.current = null;
    } else {
      throw new Error('No OTP requested');
    }
  }, []);

  return {
    isMobileWeb,
    status,
    error,
    result,
    startProvePrefill,
    submitOtpCode,
    reset,
  };
}

export default useProvePrefill;
